import { ApiEndpoints } from 'models/api';
import { BlockSetting, FacilityBlockSetting } from 'models/models';
import { standardApi } from 'store/api';
import { BlockPatternDetailsPOST } from 'pages/Block/components';

import { ShortOption } from './FilterService';

interface BlockForecastRequest {
	start_date: string;
	end_date: string;
	procedures: string[];
	tot_threshold: number;
	growth_factor: number;
	annualize?: boolean;
	block_length: number;
	volume_threshold?: number;
	cysto_rooms: ShortOption[];
	facility_breakdown: boolean;
	facilities: number[];
	groupby?: { value: string; label: string }[];
}

export interface BlockFillRateRequest {
	facility_id: number | null;
	healthsystem_id: number | null;
	block_id: number;
}

type BlockScheduleWithAddOnProxyItem = {
	label: Date;
	scheduled_in_block_minutes: number;
	estimated_add_on_minutes: number;
	allocated_minutes: number;
};

// this corresponds to a row in the fill rate details table
type BlockScheduleDetailItem = {
	id: number;
	date: Date;
	scheduled_in_block_minutes: number;
	scheduled_out_block_minutes: number;
	scheduled_turnover_minutes: number;
	allocated_minutes: number;
	number_of_rooms: number;
	fill_rate: number;
	number_of_scheduled_cases: number;
	day_of_week: string;
};

export interface BlockFillRateResponse {
	block_fill_rate_with_add_on_proxy: BlockScheduleWithAddOnProxyItem[];
	block_schedule_details: BlockScheduleDetailItem[];
}

export interface BlockForecastResponse {
	forecast: any;
	pdf_data: {
		surgeon_name: string;
		service_line: string;
		facility_name: string;
		block_length: number;
		tot_threshold: number;
		start_date: string;
		end_date: string;
		volume_threshold: number;
		annualization_factor: number;
		growth_factor: number;
		healthsystem_name: string;
		volume_spread_dow: { x: string; y: number }[];
		monthly_case_volume: { x: string; y: number }[];
		block_forecast: { index_value: string; column_value: number[] }[];
		elective_cases: { index_value: string; column_value: number[] }[];
		add_on_cases: { index_value: string; column_value: number[] }[];
	}[];
	surgeon_names: string[];
}

const blocks = standardApi.enhanceEndpoints({ addTagTypes: ['Core', 'Blocks'] });

/**
 * Provides endpoints for retrieving and setting block settings
 */
export const blockServiceApi = blocks.injectEndpoints({
	endpoints: (build) => ({
		requestBlockForecast: build.mutation<BlockForecastResponse, BlockForecastRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_BLOCK_FORECAST,
				method: 'POST',
				body,
			}),
		}),
		requestBlockForecastScorecard: build.mutation<BlockForecastResponse, BlockForecastRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_BLOCK_FORECAST_SCORECARD,
				method: 'POST',
				body,
			}),
		}),
		getBlockFillRate: build.query<BlockFillRateResponse, BlockFillRateRequest>({
			query: (body) => ({
				url: ApiEndpoints.GET_BLOCK_FILL_RATE,
				method: 'POST',
				body,
			}),
			providesTags: ['Blocks'],
		}),
	}),
});

export const { useRequestBlockForecastMutation, useGetBlockFillRateQuery, useRequestBlockForecastScorecardMutation } =
	blockServiceApi;

export const {
	endpoints: { requestBlockForecast, getBlockFillRate, requestBlockForecastScorecard },
} = blockServiceApi;
